import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  IconButton, Box, Typography, Divider, Tooltip, Collapse, Badge,
} from '@mui/material'
import {
  Home, TableChart, BarChart, CloudUpload,
  ChevronLeft, ChevronRight, ExpandMore, ExpandLess,
  SwapHoriz, EventNote, Speed, Analytics, Label,
  TrendingUp, Inventory2, Calculate, GridView, CalendarMonth,
} from '@mui/icons-material'
import { api } from '../../services/api'

const EXPANDED_WIDTH = 240
const COLLAPSED_WIDTH = 72

const PRIMARY_NAV = [
  { label: 'Dashboard', icon: Home, path: '/' },
  { label: 'Plans', icon: TableChart, path: '/plans' },
  { label: 'Reports', icon: BarChart, path: '/reports' },
]

const SUPPORTING_DATA = [
  { label: 'Changeover Times', icon: SwapHoriz, path: '/supporting/changeover' },
  { label: 'Capacity', icon: EventNote, path: '/supporting/capacity' },
  { label: 'Item Run Rates', icon: Speed, path: '/supporting/item-run-rates' },
  { label: 'Capacity Run Rates', icon: Analytics, path: '/supporting/capacity-run-rates' },
  { label: 'Item Data', icon: Label, path: '/supporting/item-data' },
  { label: 'Demand', icon: TrendingUp, path: '/supporting/demand' },
  { label: 'Current Inventory', icon: Inventory2, path: '/supporting/inventory' },
  { label: 'EC Conversion Table', icon: Calculate, path: '/supporting/ec-table' },
  { label: 'Pallet Count', icon: GridView, path: '/supporting/pallet-count' },
  { label: 'Business Calendar', icon: CalendarMonth, path: '/supporting/calendar' },
]

const SECONDARY_NAV = [
  { label: 'Data Hub', icon: CloudUpload, path: '/data' },
]

function NavItem({ item, collapsed, active }) {
  const navigate = useNavigate()
  const button = (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <ListItemButton
        onClick={() => navigate(item.path)}
        sx={{
          minHeight: 44,
          justifyContent: collapsed ? 'center' : 'initial',
          px: 2,
          mx: 1,
          borderRadius: 2,
          bgcolor: active ? 'primary.main' : 'transparent',
          color: active ? '#FFFFFF' : 'text.secondary',
          '&:hover': { bgcolor: active ? 'primary.main' : 'action.hover' },
        }}
      >
        <ListItemIcon sx={{
          minWidth: 0,
          mr: collapsed ? 0 : 2,
          justifyContent: 'center',
          color: 'inherit',
          opacity: active ? 1 : 0.7,
        }}>
          {item.stale ? (
            <Badge variant="dot" color="error" sx={{ '& .MuiBadge-dot': { width: 8, height: 8, borderRadius: '50%' } }}>
              <item.icon fontSize="small" />
            </Badge>
          ) : (
            <item.icon fontSize="small" />
          )}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: active ? 600 : 400,
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  )

  return collapsed ? (
    <Tooltip key={item.path} title={item.label} placement="right">{button}</Tooltip>
  ) : button
}

export default function LeftNav({ collapsed, onToggle }) {
  const location = useLocation()
  const [accordionOpen, setAccordionOpen] = useState(() => {
    const stored = localStorage.getItem('supportingDataOpen')
    return stored === 'true' || location.pathname.startsWith('/supporting')
  })
  const [staleItems, setStaleItems] = useState(new Set())
  const width = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH

  useEffect(() => {
    if (location.pathname.startsWith('/supporting')) {
      setAccordionOpen(true)
    }
  }, [location.pathname])

  useEffect(() => {
    localStorage.setItem('supportingDataOpen', String(accordionOpen))
  }, [accordionOpen])

  useEffect(() => {
    api.get('/data/freshness?plant=FBO').then(data => {
      if (!data) return
      const stale = new Set()
      for (const [key, val] of Object.entries(data)) {
        if (val?.state === 'stale') stale.add(key)
      }
      setStaleItems(stale)
    }).catch(() => {})
  }, [])

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const hasAnyStaleSupportingData = staleItems.size > 0

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          transition: 'width 200ms ease-in-out',
          overflowX: 'hidden',
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 1.5, minHeight: 56 }}>
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1, flex: 1 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '8px', bgcolor: '#003F0A',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <TableChart sx={{ color: '#FFFFFF', fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 500, fontSize: '18px', color: '#003F0A' }}>
              Planning
            </Typography>
          </Box>
        )}
        {collapsed && (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '8px', bgcolor: '#003F0A',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <TableChart sx={{ color: '#FFFFFF', fontSize: 20 }} />
            </Box>
          </Box>
        )}
        <IconButton onClick={onToggle} size="small">
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>

      <Divider />

      <List sx={{ pt: 1 }}>
        {PRIMARY_NAV.map(item => (
          <NavItem key={item.path} item={item} collapsed={collapsed} active={isActive(item.path)} />
        ))}
      </List>

      <Divider sx={{ mx: 2, my: 0.5 }} />

      {!collapsed ? (
        <>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => setAccordionOpen(prev => !prev)}
              sx={{ px: 2, mx: 1, borderRadius: 2, minHeight: 40 }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: 2, opacity: 0.7 }}>
                {hasAnyStaleSupportingData ? (
                  <Badge variant="dot" color="error" sx={{ '& .MuiBadge-dot': { width: 8, height: 8 } }}>
                    <EventNote fontSize="small" />
                  </Badge>
                ) : (
                  <EventNote fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText primary="Supporting Data" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }} />
              {accordionOpen ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />}
            </ListItemButton>
          </ListItem>
          <Collapse in={accordionOpen} timeout="auto">
            <List disablePadding sx={{ pl: 1 }}>
              {SUPPORTING_DATA.map(item => (
                <NavItem key={item.path} item={{ ...item, stale: staleItems.has(item.label.toLowerCase()) }} collapsed={collapsed} active={isActive(item.path)} />
              ))}
            </List>
          </Collapse>
        </>
      ) : (
        <Tooltip title="Supporting Data" placement="right">
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => setAccordionOpen(prev => !prev)}
              sx={{ justifyContent: 'center', mx: 1, borderRadius: 2, minHeight: 44 }}
            >
              <Badge variant="dot" color={hasAnyStaleSupportingData ? 'error' : 'default'} invisible={!hasAnyStaleSupportingData}>
                <EventNote fontSize="small" sx={{ opacity: 0.7 }} />
              </Badge>
            </ListItemButton>
          </ListItem>
        </Tooltip>
      )}

      <Divider sx={{ mx: 2, my: 0.5 }} />

      <List>
        {SECONDARY_NAV.map(item => (
          <NavItem key={item.path} item={{ ...item, stale: staleItems.size > 0 }} collapsed={collapsed} active={isActive(item.path)} />
        ))}
      </List>
    </Drawer>
  )
}
